import { storeSalesGoalsService } from './storeSalesGoalsService';

export class SchedulerService {
  private static instance: SchedulerService;
  private intervalId: NodeJS.Timeout | null = null;
  private isRunning = false;

  static getInstance(): SchedulerService {
    if (!SchedulerService.instance) {
      SchedulerService.instance = new SchedulerService();
    }
    return SchedulerService.instance;
  }

  // Start the scheduler to check for monthly report generation
  start(): void {
    if (this.isRunning) {
      console.log('Scheduler is already running');
      return;
    }

    console.log('Starting monthly report scheduler...');
    this.isRunning = true;

    // Check every minute for the specific time (11:50 PM on last day of month)
    this.intervalId = setInterval(() => {
      this.checkForMonthlyReport();
    }, 60000); // Check every minute

    // Also check immediately when starting
    this.checkForMonthlyReport();
  }

  // Stop the scheduler
  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isRunning = false;
    console.log('Monthly report scheduler stopped');
  }

  // Check if it's time to generate monthly report
  private async checkForMonthlyReport(): Promise<void> {
    try {
      // Get current time in MST (Mountain Standard Time - UTC-7) / MDT (Mountain Daylight Time - UTC-6)
      // Arizona (where the stores are) doesn't observe daylight saving time, so it's always MST (UTC-7)
      const now = new Date();
      const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
      const mstTime = new Date(utc + (-7 * 3600000)); // MST is UTC-7
      
      console.log(`Current time - Local: ${now.toLocaleString()}, MST: ${mstTime.toLocaleString()}`);
      
      // Get the last day of the current month in MST
      const lastDayOfMonth = new Date(mstTime.getFullYear(), mstTime.getMonth() + 1, 0).getDate();
      
      // Check if it's the last day of the month at 11:50 PM MST
      const isLastDay = mstTime.getDate() === lastDayOfMonth;
      const isCorrectTime = mstTime.getHours() === 23 && mstTime.getMinutes() === 50;

      console.log(`Schedule check - Last day: ${isLastDay}, Correct time: ${isCorrectTime} (${mstTime.getHours()}:${mstTime.getMinutes().toString().padStart(2, '0')} MST)`);

      if (isLastDay && isCorrectTime) {
        console.log('🎯 Triggering monthly store goals report generation at 11:50 PM MST on last day of month...');
        
        // Generate the monthly report (not test mode, so goals will be reset)
        const success = await storeSalesGoalsService.generateMonthlyReport();
        
        if (success) {
          console.log('✅ Monthly store goals report generated and sent successfully');
        } else {
          console.error('❌ Failed to generate monthly store goals report');
        }
      }
    } catch (error) {
      console.error('Error in monthly report scheduler:', error);
    }
  }

  // Manual trigger for testing purposes
  async triggerMonthlyReport(): Promise<boolean> {
    console.log('Manually triggering monthly report generation...');
    return await storeSalesGoalsService.generateMonthlyReport();
  }

  // Get scheduler status
  getStatus(): { isRunning: boolean; nextCheck: string } {
    const nextCheck = new Date();
    nextCheck.setMinutes(nextCheck.getMinutes() + 1);
    
    return {
      isRunning: this.isRunning,
      nextCheck: nextCheck.toISOString()
    };
  }
}

export const schedulerService = SchedulerService.getInstance();
